/**
 * Pure helpers for the HaveIBeenPwned k-anonymity password breach check.
 * React-free so they can be unit-tested in isolation (the debounce/fetch/abort
 * wiring lives in `use-password-breach-check.ts`).
 *
 * Docs: https://haveibeenpwned.com/API/v3#PwnedPasswords
 */

/**
 * SHA-1 a string and return upper-case hex, or null when Web Crypto is
 * unavailable (insecure context). HIBP keys its range API on SHA-1.
 */
export async function sha1HexUpper(input: string): Promise<string | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    return null;
  }

  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-1', data);

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

/**
 * Parse a HIBP range response (`SUFFIX:COUNT` lines) and return the breach
 * count for `hashSuffix`, or 0 if absent. Case-insensitive; tolerant of CRLF
 * and blank lines. This is the k-anonymity match — the full hash is never sent;
 * only the 5-char prefix goes to the API and the suffix is matched here.
 */
export function parseBreachCount(
  rangeBody: string,
  hashSuffix: string,
): number {
  const target = hashSuffix.toUpperCase();

  const match = rangeBody
    .split('\n')
    .map((line) => line.trim().split(':'))
    .find(([suffix]) => suffix?.toUpperCase() === target);

  return match ? Number.parseInt(match[1] ?? '0', 10) : 0;
}
